import { VERTICAL_TYPE_QUERY_PARAM } from '@rma/dashboard/generic/util/router-utils';
import { LEASING, SALES, VerticalType } from '@rma/generic/domain/types';
import { RmaIcons } from '@rma/generic/ui/icon';
import { NavigationUserType } from './nav-items-misc';
import { createNavigationItem, createNavigationMenu, createNavigationSubItem, NavigationGroupItem } from './side-nav-items';

export const COLLECT_GROUP_FEATURES = ['reviews', 'leasing', 'importReviews', 'properties', 'nps', 'pending'] as const;

export type CollectGroupFeature = (typeof COLLECT_GROUP_FEATURES)[number];

type IterableCollectGroupFeature = Exclude<CollectGroupFeature, 'importReviews' | 'pending'>[];

export function getCollectGroupItems(
  features: CollectGroupFeature[],
  userType: NavigationUserType,
  reviewsVerticalType: VerticalType = SALES,
): NavigationGroupItem[] {
  const leasing = features.includes('leasing');
  const hasImportReviews = features.includes('importReviews');
  const hasPending = features.includes('pending');

  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const filtered = features.filter((f) => !['importReviews', 'pending'].includes(f)) as IterableCollectGroupFeature;

  const stub = userType === 'team' ? 'agency' : userType;

  return filtered.map((f) => {
    switch (f) {
      case 'reviews':
        return getReviewItems(stub, reviewsVerticalType, hasImportReviews, hasPending);
      case 'leasing':
        return getReviewItems(stub, LEASING, hasImportReviews, false);
      case 'properties':
        return getPropertiesItems(leasing);
      case 'nps':
        return createNavigationItem('nps', 'survey', 'speed');
    }
  });
}

function getReviewItems(userType: NavigationUserType, verticalType: VerticalType, canImport = true, hasPending = false) {
  const opportunitiesBaseUrl = 'reviews/request/opportunities';

  const label = getReviewLabel('reviews', verticalType);
  const queryParams = verticalType ? { verticalType } : undefined;
  const queryString = verticalType ? `?${VERTICAL_TYPE_QUERY_PARAM}=${verticalType}` : '';

  const icon: RmaIcons = verticalType !== LEASING ? 'rma-reviews' : 'rma-for-sale';

  const children = [
    createNavigationSubItem(getReviewLabel('reviewsCompleted'), '/reviews', { ...queryParams, status: 'Completed' }, [
      `reviews\\${queryString}*`,
    ]),
    createNavigationSubItem(getReviewLabel('reviewsRequest'), `/${opportunitiesBaseUrl}`, queryParams, [
      `${opportunitiesBaseUrl}\\${queryString}*`,
    ]),
  ];

  const pathsInArea = [formatUrl('reviews/*', userType, verticalType)];

  if (canImport) {
    const importBaseUrl = 'reviews/import';
    children.push(
      createNavigationSubItem(getReviewLabel('reviewsImport'), `/${importBaseUrl}`, queryParams, [
        `${importBaseUrl}/(external|existing)${queryString ? `\\${queryString}` : ''}`,
      ]),
    );
  }

  if (hasPending) {
    children.push(
      createNavigationSubItem(getReviewLabel('reviewsPending'), '/reviews', {
        ...queryParams,
        status: 'Pending',
      }),
    );
  }

  return createNavigationMenu(label, icon, children, pathsInArea);
}

function getPropertiesItems(hasLeasing = false) {
  if (hasLeasing) {
    return createNavigationMenu('properties', 'cottage', [
      createNavigationSubItem('propertySales', listingUrl('sales'), undefined, [listingUrl('sales'), `${listingUrl('sales')}/\\*`]),
      createNavigationSubItem('propertyLeasing', listingUrl('leasing'), undefined, [listingUrl('leasing'), `${listingUrl('leasing')}/\\*`]),
    ]);
  }

  const pathsInRoutes = [listingUrl('sales'), `${listingUrl('sales')}/*`];

  return createNavigationItem('properties', listingUrl('sales'), 'cottage', pathsInRoutes);
}

function listingUrl(verticalType: 'sales' | 'leasing') {
  return `/campaigns/${verticalType}`;
}

function formatUrl(subpath: string, userType: NavigationUserType, verticalType?: 'sales' | 'leasing' | VerticalType) {
  const path = [userType, verticalType?.toLocaleLowerCase(), subpath].filter(Boolean).join('/');
  return `/${path}`;
}

function getReviewLabel(label: string, verticalType?: VerticalType): string {
  if (!verticalType) {
    return label;
  }
  const pascalCaseLabel = label.charAt(0).toUpperCase() + label.slice(1);
  return `${verticalType.toLocaleLowerCase()}${pascalCaseLabel}`;
}
